import React, {useState, useEffect, useRef} from 'react'
import {useIntl} from 'react-intl'
import {useLanguage} from '../../../../_gori/i18n/Metronici18n'
import {PREFERRED_LANGUAGE_KEY} from '../../../../_gori/config/LanguageConfig'

interface Language {
  code: string
  name: string
  flag: string
}

const LanguageToggle: React.FC = () => {
  const intl = useIntl()
  const {setLanguage} = useLanguage()
  const [isOpen, setIsOpen] = useState(false)
  const [currentLocale, setCurrentLocale] = useState(intl.locale)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const languages: Language[] = [
    {code: 'en', name: 'English', flag: '/media/gori/language-toggle-flags/united-states.svg'},
    {code: 'zh', name: 'Simplified Chinese', flag: '/media/gori/language-toggle-flags/china.svg'},
    {code: 'ja', name: 'Japanese', flag: '/media/gori/language-toggle-flags/japan.svg'},
  ]

  const getLanguageNames = (locale: string) => {
    switch (locale) {
      case 'zh':
        return {en: '英语', zh: '简体中文', ja: '日语'}
      case 'ja':
        return {en: '英語', zh: '中国語（簡体字）', ja: '日本語'}
      default:
        return {en: 'English', zh: 'Simplified Chinese', ja: 'Japanese'}
    }
  }

  const languageNames = getLanguageNames(currentLocale)
  const currentLanguage = languages.find((lang) => lang.code === currentLocale) || languages[0]

  const handleLanguageChange = (languageCode: string) => {
    localStorage.setItem(PREFERRED_LANGUAGE_KEY, languageCode)
    setLanguage(languageCode)
    setCurrentLocale(languageCode)
    setIsOpen(false)

    // Dispatch custom event to notify other components
    window.dispatchEvent(
      new CustomEvent('preferredLanguageChanged', {
        detail: {language: languageCode},
      })
    )
  }

  useEffect(() => {
    const stored = localStorage.getItem(PREFERRED_LANGUAGE_KEY)
    if (stored) {
      setCurrentLocale(stored)
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  return (
    <div id='language-toggle' className='position-fixed language-toggle'>
      <div className='dropdown language-toggle__dropdown' ref={dropdownRef}>
        <button
          type='button'
          className='btn d-flex align-items-center language-toggle__btn'
          aria-haspopup='true'
          aria-expanded={isOpen}
          aria-label='Change language'
          onClick={() => setIsOpen(!isOpen)}
        >
          <img
            src={currentLanguage.flag}
            alt={currentLanguage.name}
            width='20'
            height='20'
            className='language-toggle__flag'
          />
          <i className='fas fa-chevron-down language-toggle__chevron' aria-hidden='true'></i>
        </button>

        {isOpen && (
          <div className='position-absolute mb-2 language-toggle__menu' role='menu'>
            {languages.map((lang, index) => {
              const isActive = lang.code === currentLocale
              return (
                <button
                  key={lang.code}
                  type='button'
                  className={
                    'btn w-100 d-flex align-items-center language-toggle__item' +
                    (isActive ? ' is-active' : '') +
                    (index === 0 ? ' is-first' : '') +
                    (index === languages.length - 1 ? ' is-last' : '')
                  }
                  role='menuitem'
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <img
                    src={lang.flag}
                    alt={languageNames[lang.code as keyof typeof languageNames]}
                    width='20'
                    height='20'
                    className='language-toggle__flag'
                  />
                  <span className='language-toggle__label'>
                    {languageNames[lang.code as keyof typeof languageNames]}
                  </span>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default LanguageToggle
